import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Film, Trash2, Play, ImageIcon } from "lucide-react";

type FoilWithMedia = {
  id: string;
  foil_key: string;
  foil_label: string;
  preview_video_url: string | null;
  preview_image_url: string | null;
};

const FoilVideoSettings = () => {
  const [foils, setFoils] = useState<FoilWithMedia[]>([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState<string | null>(null);
  const [dragOverId, setDragOverId] = useState<string | null>(null);

  const fetchFoils = async () => {
    const { data } = await supabase
      .from("foil_pricing")
      .select("id, foil_key, foil_label, preview_video_url, preview_image_url")
      .order("foil_label", { ascending: true });
    if (data) setFoils(data as FoilWithMedia[]);
    setLoading(false);
  };

  useEffect(() => { fetchFoils(); }, []);

  const uploadFile = async (foilId: string, foilKey: string, file: File, type: "video" | "image") => {
    const isVideo = file.type.startsWith("video/");
    const isImage = file.type.startsWith("image/");

    if (type === "video" && !isVideo) {
      toast.error("Tylko pliki wideo (MP4, MOV, WEBM)");
      return;
    }
    if (type === "image" && !isImage) {
      toast.error("Tylko pliki graficzne (JPG, PNG, WEBP)");
      return;
    }
    if (file.size > 50 * 1024 * 1024) {
      toast.error("Maks. rozmiar pliku: 50 MB");
      return;
    }

    setUploading(`${foilId}-${type}`);

    const foil = foils.find(f => f.id === foilId);
    const oldUrl = type === "video" ? foil?.preview_video_url : foil?.preview_image_url;
    if (oldUrl) {
      const oldPath = oldUrl.split("/gallery/").pop();
      if (oldPath) await supabase.storage.from("gallery").remove([decodeURIComponent(oldPath)]);
    }

    const ext = file.name.split(".").pop();
    const fileName = `foil-${type}s/${foilKey}-${Date.now()}.${ext}`;
    const { error: uploadError } = await supabase.storage.from("gallery").upload(fileName, file);

    if (uploadError) {
      toast.error("Błąd przesyłania pliku");
      setUploading(null);
      return;
    }

    const { data: urlData } = supabase.storage.from("gallery").getPublicUrl(fileName);
    const field = type === "video" ? "preview_video_url" : "preview_image_url";
    await supabase.from("foil_pricing").update({ [field]: urlData.publicUrl }).eq("id", foilId);

    setFoils(prev => prev.map(f => f.id === foilId ? { ...f, [field]: urlData.publicUrl } : f));
    setUploading(null);
    toast.success(type === "video" ? "Wideo dodane" : "Zdjęcie dodane");
  };

  const handleDrop = (e: React.DragEvent, foilId: string, foilKey: string) => {
    e.preventDefault();
    setDragOverId(null);
    const file = e.dataTransfer.files?.[0];
    if (!file) return;
    const type = file.type.startsWith("video/") ? "video" : "image";
    uploadFile(foilId, foilKey, file, type);
  };

  const handleDelete = async (foilId: string, type: "video" | "image") => {
    const foil = foils.find(f => f.id === foilId);
    const url = type === "video" ? foil?.preview_video_url : foil?.preview_image_url;
    if (url) {
      const path = url.split("/gallery/").pop();
      if (path) await supabase.storage.from("gallery").remove([decodeURIComponent(path)]);
    }
    const field = type === "video" ? "preview_video_url" : "preview_image_url";
    await supabase.from("foil_pricing").update({ [field]: null }).eq("id", foilId);
    setFoils(prev => prev.map(f => f.id === foilId ? { ...f, [field]: null } : f));
    toast.success(type === "video" ? "Wideo usunięte" : "Zdjęcie usunięte");
  };

  if (loading) return <p className="text-muted-foreground text-sm">Ładowanie...</p>;

  return (
    <div className="bg-card border border-border rounded-xl p-6 space-y-5">
      <div className="flex items-center gap-3">
        <Film className="w-5 h-5 text-accent" />
        <h2 className="text-lg font-bold text-foreground">Media podglądu folii</h2>
      </div>
      <p className="text-muted-foreground text-sm">
        Dodaj wideo lub zdjęcie pokazujące jak wygląda każda folia. Przeciągnij plik na wybraną folię lub użyj przycisków.
      </p>

      <div className="space-y-2">
        {foils.map((foil) => (
          <div
            key={foil.id}
            onDragOver={(e) => { e.preventDefault(); setDragOverId(foil.id); }}
            onDragLeave={() => setDragOverId(null)}
            onDrop={(e) => handleDrop(e, foil.id, foil.foil_key)}
            className={`bg-background rounded-lg p-3 border-2 border-dashed transition-colors space-y-2 ${
              dragOverId === foil.id
                ? "border-accent bg-accent/5"
                : "border-border"
            }`}
          >
            <div className="flex items-center gap-4">
              {/* Thumbnail */}
              <div className="w-20 h-14 rounded-lg overflow-hidden bg-muted shrink-0 flex items-center justify-center">
                {foil.preview_video_url ? (
                  <video
                    src={foil.preview_video_url}
                    className="w-full h-full object-cover"
                    muted
                    playsInline
                    onMouseEnter={(e) => (e.target as HTMLVideoElement).play()}
                    onMouseLeave={(e) => { const v = e.target as HTMLVideoElement; v.pause(); v.currentTime = 0; }}
                  />
                ) : foil.preview_image_url ? (
                  <img src={foil.preview_image_url} alt={foil.foil_label} className="w-full h-full object-cover" />
                ) : (
                  <Play className="w-5 h-5 text-muted-foreground/40" />
                )}
              </div>

              {/* Label */}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{foil.foil_label}</p>
                <p className="text-xs text-muted-foreground">
                  {foil.preview_video_url && foil.preview_image_url
                    ? "✓ Wideo + Zdjęcie"
                    : foil.preview_video_url
                    ? "✓ Wideo"
                    : foil.preview_image_url
                    ? "✓ Zdjęcie"
                    : "Przeciągnij plik tutaj..."}
                </p>
              </div>
            </div>

            {/* Media actions row */}
            <div className="flex items-center gap-3 flex-wrap pl-24">
              {/* Video upload */}
              <div className="flex items-center gap-1">
                <label className={`cursor-pointer ${uploading === `${foil.id}-video` ? "pointer-events-none opacity-50" : ""}`}>
                  <Button variant="outline" size="sm" className="h-7 text-xs pointer-events-none" asChild>
                    <span>
                      <Film className="w-3 h-3 mr-1" />
                      {uploading === `${foil.id}-video` ? "..." : foil.preview_video_url ? "Zmień wideo" : "Dodaj wideo"}
                    </span>
                  </Button>
                  <input type="file" accept="video/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadFile(foil.id, foil.foil_key, e.target.files[0], "video")} />
                </label>
                {foil.preview_video_url && (
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDelete(foil.id, "video")}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>

              {/* Image upload */}
              <div className="flex items-center gap-1">
                <label className={`cursor-pointer ${uploading === `${foil.id}-image` ? "pointer-events-none opacity-50" : ""}`}>
                  <Button variant="outline" size="sm" className="h-7 text-xs pointer-events-none" asChild>
                    <span>
                      <ImageIcon className="w-3 h-3 mr-1" />
                      {uploading === `${foil.id}-image` ? "..." : foil.preview_image_url ? "Zmień zdjęcie" : "Dodaj zdjęcie"}
                    </span>
                  </Button>
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && uploadFile(foil.id, foil.foil_key, e.target.files[0], "image")} />
                </label>
                {foil.preview_image_url && (
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive hover:text-destructive" onClick={() => handleDelete(foil.id, "image")}>
                    <Trash2 className="w-3 h-3" />
                  </Button>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default FoilVideoSettings;
